import {Box} from '@mui/material';
import TextFieldComponent from '../../../common/TextFieldComponent/TextFieldComponent';
import TextAreaComponent from '../../../common/TextAreaComponent/TextAreaComponent';
import './JobDetailStep.scss';
import {JobDetailData} from '../NewJobDetail';
import {t} from 'i18next';
import AssignToGroup from './AssignToGroup';
import {ISelectItem} from '../../../../../../shared/types';
import {useEffect, useState} from 'react';
import {useGetReviewJobGroups} from '../../../../../../hooks/ai/review/useReview';

interface JobDetailStepProps {
  data: JobDetailData;
  setData: (data: JobDetailData) => void;
  hasSpecialCharError: boolean;
  isCreatingGroup: boolean;
  setIsCreatingGroup: React.Dispatch<React.SetStateAction<boolean>>;
}
const STEP = 'jobName';
const DESCRIPTION = 'description';
const SELECTED_GROUP = 'selectedGroup';
const JobDetailStep = ({
  data,
  setData,
  hasSpecialCharError,
  isCreatingGroup,
  setIsCreatingGroup,
}: JobDetailStepProps) => {
  const [pendingGroupName, setPendingGroupName] = useState<string | null>(null);

  const {data: reviewJobGroupsResponse} = useGetReviewJobGroups();
  const handleChange = (
    field: keyof JobDetailData,
    value: string | ISelectItem | null
  ) => {
    setData({
      ...(data || {}),
      [field]: value,
    });
    setIsCreatingGroup(false);
  };

  const apiGroups =
    reviewJobGroupsResponse?.jobGroups?.map((jg) => ({
      name: jg.name,
      label: jg.name,
      id: jg.jobGroupId,
    })) ?? [];

  useEffect(() => {
    if (!pendingGroupName) return;
    const match = apiGroups.find((g) => g.name === pendingGroupName);
    if (match) {
      handleChange(SELECTED_GROUP, match);
      setPendingGroupName(null);
    }
  }, [apiGroups, pendingGroupName]);

  const handleGroupList = (groupName: string) => {
    setPendingGroupName(groupName);
  };

  const selectedGroup =
    apiGroups?.find((g) => g.id === Number(data?.selectedGroup?.id)) ??
    data?.selectedGroup;

  return (
    <Box className="job-details-container">
      <Box className="left-section-container">
        <TextFieldComponent
          headerLabel={t('ai.review.jobList.jobListColumns.jobName')}
          isRequired={true}
          value={data?.jobName}
          slotProps={{
            input: {
              inputProps: {maxLength: 50},
            },
            formHelperText: {
              'aria-label': `${data?.jobName?.length} ${t('ai.review.newJobDetail.outOf50Characters')}`,
            },
          }}
          state={hasSpecialCharError ? 'error' : 'enabled'}
          onChange={(e) => handleChange(STEP, e.target.value)}
          label={t('ai.review.newJobDetail.jobNamePlaceholder')}
          showErrorIcon={true}
          supportText={
            hasSpecialCharError
              ? t('ai.review.newJobDetail.splCharError')
              : `${data?.jobName?.length} / 50`
          }
          headerAriaLabel={t('ai.review.newJobDetail.jobNameIsRequired')}
          tabIndex={0}
          aria-live="polite"
        />
        <TextAreaComponent
          placeholder={t('ai.review.newJobDetail.descPlaceholder')}
          label={t('ai.review.newJobDetail.description')}
          data-testid="description-input"
          value={data?.description}
          maxLength={200}
          helperText={`${data?.description?.length} / 200`}
          aria-label={`${data?.description?.length} ${t('ai.review.newJobDetail.outOf200Characters')}`}
          onChange={(value) => handleChange(DESCRIPTION, value)}
          aria-live="polite"
        />
      </Box>
      <Box className="right-section-container">
        <AssignToGroup
          groupList={apiGroups}
          handleGroupList={handleGroupList}
          selectedGroup={selectedGroup}
          setSelectedGroup={(group) => handleChange(SELECTED_GROUP, group)}
          isCreatingGroup={isCreatingGroup}
          setIsCreatingGroup={setIsCreatingGroup}
        />
      </Box>
    </Box>
  );
};

export default JobDetailStep;
